import { ReactElement, ReactNode, RefObject, useCallback, useEffect, useRef, useState } from 'react';
import useAbortController from '@hooks/useAbortController';
import { useTranslations } from 'next-intl';
import { getPromotionProducts } from '@api/interfaces/productApi';
import Product from '@molecules/Product/Product';
import { trackProductImpressionsWithPosition } from '@helpers/analyticsHelpers/trackProductImpressions';
import Config from '@config';
import Carousel, { ItemType } from '@molecules/Carousel/Carousel';
import debounce from '@helpers/debounce';
import useCustomer from '@hooks/useCustomer';
import {
  StyledMixMatchBeam,
  StyledMixMatchBeamArrow,
  StyledMixMatchBeamArrowContainer,
  StyledSpinner,
} from './MixMatchBeam.styles';
import type { AxfoodBasicProductViewModel, AxfoodProductDetailsViewModel } from '@occ/api-client';
import { useMediaQuery } from 'react-responsive';

// Define a type for the props of the Product component
type ProductElementProps = {
  product: AxfoodBasicProductViewModel | AxfoodProductDetailsViewModel;
};

// Define a type for the ReactElement that has the product prop
type ProductElement = ReactElement<ProductElementProps>;

interface Props {
  product: AxfoodProductDetailsViewModel | AxfoodBasicProductViewModel;
  onClose: () => void;
  listPos: number;
  closeBeam?: boolean;
  gridRef?: RefObject<HTMLDivElement | null>;
  productRef: RefObject<HTMLDivElement | null>;
}

const MixMatchBeam = ({ product, onClose, listPos, closeBeam, gridRef, productRef }: Props) => {
  const { customer } = useCustomer();
  const isLoggedIn = !customer?.isAnonymous;
  const [row, setRow] = useState<number>(0);
  const [arrowPosition, setArrowPosition] = useState<number | null>(null);
  const [productsPerRow, setProductsPerRow] = useState<number>(0);
  const [showSpinner, setShowSpinner] = useState(true);
  const [visible, setVisible] = useState(false);
  const [productElements, setProductElements] = useState<ReactNode[]>([]);
  const [trackedProducts, setTrackedProducts] = useState<Array<string>>([]);
  const [productHeight, setProductHeight] = useState(0);
  const t = useTranslations('product');
  const mixMatchRef = useRef<HTMLDivElement>(null);
  const { getSignal } = useAbortController();

  const getAmountOfItemsPerSlide = useCallback(() => {
    const gridWidth = gridRef?.current?.offsetWidth;
    const productWidth = productRef?.current?.offsetWidth;

    if (gridWidth && productWidth) {
      return Math.floor(gridWidth / productWidth);
    }
    return 0;
  }, [gridRef, productRef]);

  const calculateRowColumn = useCallback(() => {
    const numberOfProducts = getAmountOfItemsPerSlide();
    setProductsPerRow(numberOfProducts);

    // Calculate which grid row the MixMatchBeam should appear in
    // The beam appears in the row after the product that triggered it
    // +1 ensures it appears below the triggering product
    const rowValue = Math.ceil(listPos / numberOfProducts) + 1;
    setRow(rowValue);

    // Calculate the column position for the arrow indicator
    // This positions the arrow to point at the triggering product
    // Handle edge case where product is at the end of a row (modValue = 0)
    const columnPosition = listPos % numberOfProducts === 0 ? numberOfProducts : listPos % numberOfProducts;

    // Convert to percentage and center the arrow on the product
    // -1/numberOfProducts/2 centers the arrow on the product width
    const newArrowPosition = (columnPosition / numberOfProducts - 1 / numberOfProducts / 2) * 100;

    // Percentage from left side of page
    setArrowPosition(newArrowPosition);
  }, [listPos, getAmountOfItemsPerSlide]);

  useEffect(() => {
    // Parent component is closing the beam
    if (closeBeam === true) {
      setVisible(false);
      // Wait for CSS animation to complete before unmounting the component
      setTimeout(onClose, Config.TIMEOUT.mixMatchBeamAnimationMs);
    }
  }, [closeBeam, onClose]);

  // Calculate productsPerRow when refs become available
  useEffect(() => {
    if (gridRef?.current && productRef?.current) {
      calculateRowColumn();
      setProductHeight(productRef.current.offsetHeight);
    }
  }, [gridRef, productRef, calculateRowColumn]);

  const isFromDesktop = useMediaQuery({ query: Config.BREAKPOINTS.FROM_DESKTOP });

  useEffect(() => {
    if (mixMatchRef.current && productRef?.current && visible) {
      // When the MixMatchBeam opens:
      //   • If both the parent product and the MixMatchBeam fit in the viewport,
      //     center them together.
      //   • Otherwise, center only the MixMatchBeam.

      // 1. Calculate available viewport height (account for sticky banner)
      const stickyBannerHeight = Config.HEIGHTS.STICKY_TOOLBAR * (isFromDesktop ? 1 : 2);
      const availableViewportHeight = window.innerHeight - stickyBannerHeight;

      // 2. Calculate estimated mix match height
      // We want to start scrolling before animation is complete to ensure a smooth experience.
      // MixMatchBeam height is not updated immediately therefore total height is estimated based on
      // parent product card height plus estimated padding and margin for MixAndMatch Beam
      const { offsetHeight: parentProductHeight } = productRef.current;
      const totalBeamPadding = 140;
      const estimatedMixMatchHeight = parentProductHeight + totalBeamPadding;

      // 3. Calculate and scroll to new position
      // Center both components if there's enough space in the viewport

      if (parentProductHeight + estimatedMixMatchHeight < availableViewportHeight) {
        const totalHeight = parentProductHeight + estimatedMixMatchHeight;
        // Determine the parent product’s position (the top edge of the area we want to center)
        // relative to the entire document.
        // Note: getBoundingClientRect().top returns the element’s offset from the viewport,
        // so add window.scrollY to include the current vertical scroll position.

        const parentProductRect = productRef.current.getBoundingClientRect();
        const parentProductTopPosition = parentProductRect.top + window.scrollY;

        // Center content in the viewport
        // Formula: scroll to position where content starts, minus sticky banner offset,
        // minus half the remaining space (to center the content vertically)
        const scrollPosition =
          parentProductTopPosition - stickyBannerHeight - (availableViewportHeight - totalHeight) / 2;

        if (scrollPosition > 0) {
          window.scrollTo({
            top: scrollPosition,
            behavior: 'smooth',
          });
        }
      } else {
        // Center MixMatchBeam only
        const mixMatchRect = mixMatchRef.current.getBoundingClientRect();
        const mixMatchTopPosition = mixMatchRect.top + window.scrollY;

        const scrollPosition =
          mixMatchTopPosition - stickyBannerHeight - (availableViewportHeight - estimatedMixMatchHeight) / 2;
        if (scrollPosition > 0) {
          window.scrollTo({
            top: scrollPosition,
            behavior: 'smooth',
          });
        }
      }
    }
  }, [visible, productRef, isFromDesktop]);

  useEffect(() => {
    const getProductElements = (items: AxfoodBasicProductViewModel[] = []) =>
      items.map((child) => (
        <Product product={child} key={`mix-match-${child.code}`} variant="mixmatch" disableMixMatchButton />
      ));

    setVisible(true);

    const fetchData = async (code: string) => {
      try {
        const response = await getPromotionProducts(
          {
            promotion: code,
            excludeProducts: product.code,
            page: 0,
            pageSize: 100,
          },
          getSignal()
        );
        // getPromotionProducts returns null if promotionCode is empty (B2C-35896)
        if (!response) return;
        setProductElements(getProductElements(response.data.items));
        setShowSpinner(false);
      } catch (error) {
        console.error(error);
      }
    };

    const productCode = product.potentialPromotions[0]?.code;

    if (productCode) {
      fetchData(productCode);
    }

    // Trigger initial calculation
    calculateRowColumn();
  }, [product.code, product.potentialPromotions, calculateRowColumn, getSignal]);

  useEffect(() => {
    if (!mixMatchRef.current) return;

    const debouncedCalculate = debounce(calculateRowColumn, Config.TIMEOUT.mixMatchBeamResizeMs);

    // Use ResizeObserver for both window and element resizing
    const resizeObserver = new ResizeObserver(() => {
      debouncedCalculate();
    });

    resizeObserver.observe(mixMatchRef.current);
    resizeObserver.observe(document.body);

    return () => {
      resizeObserver.disconnect();
      debouncedCalculate.cancel();
    };
  }, [calculateRowColumn]);

  const trackVisibleProducts = (items: ItemType[]) => {
    const newProducts = items
      .filter(({ item }) => {
        const productCode = (item as ProductElement)?.props?.product?.code;
        return productCode && !trackedProducts.includes(productCode);
      })
      .map(({ item, index }) => ({
        product: (item as ProductElement)?.props?.product,
        position: index + 1,
      }));

    if (newProducts.length > 0) {
      const productCodes = newProducts.map(({ product }) => product.code);
      setTrackedProducts((prev) => [...prev, ...productCodes]);
      trackProductImpressionsWithPosition(newProducts, `multi_product_offer | ${product.code}`, isLoggedIn);
    }
  };

  if (productsPerRow === 0) {
    return null;
  }

  return (
    <>
      <StyledMixMatchBeam ref={mixMatchRef} $isVisible={visible} style={{ gridRow: row, gridColumn: '1 / -1' }}>
        <StyledMixMatchBeamArrowContainer>
          <StyledMixMatchBeamArrow style={{ left: `${arrowPosition}%` }} />
        </StyledMixMatchBeamArrowContainer>
        <>
          {showSpinner ? (
            <StyledSpinner size={32} $height={productHeight} />
          ) : (
            <>
              {productElements && (
                <Carousel
                  elements={productElements}
                  elementsPerSlide={productsPerRow}
                  variant="mixmatch"
                  onSlideShown={trackVisibleProducts}
                  title={t('mixmatch.label')}
                />
              )}
            </>
          )}
        </>
      </StyledMixMatchBeam>
    </>
  );
};

export default MixMatchBeam;
